import React, { useEffect, useRef, useState } from "react";

const SimpleGoogleAutocomplete = ({
  onSelect,
  initialValue,
  countryCodes = [],
  disabled = false,
  showClearIcon = false,
  onClear,
  showLabel = true,
  searchType = "address", // address | state | city
  placeholder = "Enter address",
  selectedState = null,
}) => {
  const [input, setInput] = useState(initialValue?.address || "");
  const [suggestions, setSuggestions] = useState([]);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const mapDiv = useRef(null);

  const primaryCountry =
    countryCodes.length > 0 ? countryCodes[0].toLowerCase() : null;

  useEffect(() => {
    if (!window.google) return;

    autocompleteService.current =
      new window.google.maps.places.AutocompleteService();

    const dummyMap = new window.google.maps.Map(mapDiv.current);

    placesService.current = new window.google.maps.places.PlacesService(
      dummyMap,
    );
  }, []);

  useEffect(() => {
    setInput(initialValue?.address || "");
  }, [initialValue?.address]);

  useEffect(() => {
    if (disabled) {
      setSuggestions([]);
    }
  }, [disabled]);

  const getGoogleType = () => {
    switch (searchType) {
      case "state":
        return ["(regions)"];

      case "city":
        return ["(cities)"];

      case "address":
      default:
        return ["address"];
    }
  };

  const clearLocationData = () => {
    onClear?.();

    onSelect?.({
      address: "",
      lat: null,
      lng: null,
      cityName: "",
      stateName: "",
      countryName: "",
      streetName: "",
    });
  };

  const fetchSuggestions = (inputValue) => {
    if (!inputValue || !autocompleteService.current || disabled) {
      setSuggestions([]);
      return;
    }

    const request = {
      input: inputValue,
      types: getGoogleType(),
      componentRestrictions: primaryCountry
        ? { country: primaryCountry }
        : undefined,
    };

    // Bias city search toward selected state
    if (searchType === "city" && selectedState?.lat && selectedState?.lng) {
      request.locationBias = {
        center: {
          lat: selectedState.lat,
          lng: selectedState.lng,
        },
      };
    }

    autocompleteService.current.getPlacePredictions(
      request,
      (predictions, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !predictions
        ) {
          setSuggestions([]);
          return;
        }

        let filtered = predictions;

        // State mode → only states
        if (searchType === "state") {
          filtered = predictions.filter((item) =>
            item.types.includes("administrative_area_level_1"),
          );
        }

        setSuggestions(filtered);
      },
    );
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    if (!value.trim()) {
      setSuggestions([]);
      clearLocationData();
      return;
    }

    fetchSuggestions(value);
  };

  const getComponent = (components, types) =>
    components.find((comp) => types.some((type) => comp.types.includes(type)))
      ?.long_name || "";

  const handleSelect = (placeId) => {
    if (!placesService.current) return;

    placesService.current.getDetails({ placeId }, (place, status) => {
      if (
        status !== window.google.maps.places.PlacesServiceStatus.OK ||
        !place
      ) {
        return;
      }

      const addressComponents = place.address_components || [];

      const cityName = getComponent(addressComponents, [
        "locality",
        "sublocality_level_1",
        "administrative_area_level_2",
      ]);

      const stateName = getComponent(addressComponents, [
        "administrative_area_level_1",
      ]);

      const countryName = getComponent(addressComponents, ["country"]);

      const streetNumber = getComponent(addressComponents, ["street_number"]);

      const route = getComponent(addressComponents, ["route"]);

      const streetName = [streetNumber, route].filter(Boolean).join(" ");

      let displayValue = place.formatted_address;

      if (searchType === "state") {
        displayValue = stateName || place.name;
      }

      if (searchType === "city") {
        displayValue = cityName || place.name;
      }

      const result = {
        address: displayValue,
        lat: place.geometry?.location?.lat() || null,
        lng: place.geometry?.location?.lng() || null,
        cityName,
        stateName,
        countryName,
        streetName,
      };

      onSelect?.(result);
      setInput(displayValue);
      setSuggestions([]);
    });
  };

  const handleClear = () => {
    setInput("");
    setSuggestions([]);
    clearLocationData();
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {showLabel && (
        <label
          style={{
            fontSize: "16px",
            color: "#A6A6A6",
            marginBottom: "2%",
            display: "block",
          }}
        >
          Address
        </label>
      )}

      <div style={{ position: "relative" }}>
        <input
          value={input}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          style={{
            width: "100%",
            padding: showClearIcon && input ? "8px 35px 8px 8px" : "8px",
            border: "1px solid #ccc",
            borderRadius: "12px",
            backgroundColor: disabled ? "#f5f5f5" : "#fff",
            cursor: disabled ? "not-allowed" : "text",
          }}
          onBlur={() => {
            setTimeout(() => {
              setSuggestions([]);
            }, 150);
          }}
        />

        {showClearIcon && input && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear input"
            style={{
              position: "absolute",
              right: 8,
              top: "20%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              width: 20,
              height: 20,
              borderRadius: "50%",
              fontSize: 24,
              color: "#666",
            }}
          >
            ×
          </button>
        )}
      </div>

      {!!suggestions.length && !disabled && (
        <ul
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 999,
            background: "#fff",
            border: "1px solid #ccc",
            listStyle: "none",
            margin: 0,
            padding: 0,
            maxHeight: 220,
            overflowY: "auto",
            borderRadius: 12,
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          }}
        >
          {suggestions.map((item) => (
            <li
              key={item.place_id}
              onClick={() => handleSelect(item.place_id)}
              style={{
                padding: 12,
                cursor: "pointer",
                borderBottom: "1px solid #eee",
              }}
            >
              {item.description}
            </li>
          ))}
        </ul>
      )}

      <div ref={mapDiv} style={{ display: "none" }} />
    </div>
  );
};

export default SimpleGoogleAutocomplete;
